import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import InputField from '@components/InputField';
import ModalContainer from '@components/modals/ModalContainer';
import ScrollButtons from '@components/ScrollButtons';
import { toastSuccess } from '@components/Toast';

import AddLocationModal from '@features/design/components/AddLocationModal';
import TableActionDropdown from '@features/design/components/TableActionDropdown';
import {
	canEditBrandOrFactory,
	canEditCountry,
	canEditDropdown,
	canEditLocation,
	canEditQcApproval,
	canEditRemarks,
	canEditSampleId,
	getSampleTypeBasedOnConditions,
	isAllowedToEditInventory,
	isFactory,
	locationInfo,
	SAMPLE_STATUS,
	SAMPLE_TYPES
} from '@features/design/utils/designUtils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getBrands } from '@store/design/action';
import { getAllBrands } from '@store/reducers';

import { findAllSupplier } from '@services/costingService';
import {
	getProductSampleLocations,
	getProductSamples,
	getSampleCountryList,
	submitSampleUpdate,
	updateSampelInventory
} from '@services/designInfoUpdateService';

import ApiClient from '@lib/apiClient';

import { convertDateToLocal, printApiError, replaceUnderLine } from '@utils/Util';

const InventoryAndStickerModal = ({
	onClose,
	productIds,
	isDesignEdit,
	setIsShowInventoryModal,
	onSuccess
}) => {
	const [productSampleList, setProductSampleList] = useState([]);
	const dispatch = useDispatch();
	const [errors, setErrors] = useState([]);
	const [selectedRowId, setSelectedRowId] = useState(null);
	const [countryList, setCountryList] = useState([]);
	const [suppliersList, setSuppliersList] = useState([]);
	const [allBrandsList, setAllBrandsList] = useState([]);
	const [locationsList, setLocationsList] = useState([]);
	const [showLocationModal, setShowLocationModal] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState(null);
	const brandsList = useSelector(getAllBrands) || [];
	const [selectedSampleIds, setSelectedSampleIds] = useState([]);
	const [responseLength, setResponseLength] = useState(null);
	const isAllSelected = responseLength === selectedSampleIds?.length;
	const [activeRow, setActiveRow] = useState(null);
	const { id } = useParams();

	useEffect(() => {
		const samplesList = [...productSampleList];
		const newArr = [];
		samplesList.forEach((item) => {
			item.sampleList.forEach((sample) => {
				newArr.push(sample);
			});
		});

		const filterNewArr = newArr.filter((item) => isValidRow(item));
		setResponseLength(filterNewArr.length);
	}, [JSON.stringify(productSampleList)]);

	const scrollButtonRef = useRef(null);

	const createProductResponse = (item) => {
		return {
			id: item?.id,
			name: item?.name,
			addedByNitex: item?.addedByNitex,
			isNitexProduct: item?.isNitexProduct,
			isBasicInfoUpdated: item?.isBasicInfoUpdated,
			addedByName: item?.addedByName,
			addedAt: item?.addedAt,
			referenceNumber: item?.referenceNumber,
			category: item?.category,
			productGroup: item?.productGroup,
			designDocuments: item?.designDocuments,
			fabricName: item?.fabricName,
			isWrongEntry: item?.isWrongEntry,
			parentProductId: item?.parentProductId
		};
	};

	const organizeResponseData = (response) => {
		const samples = [];
		let rowIdCount = 0;
		const sampleItemsList = [];

		response.forEach((item) => {
			samples.push({
				...item?.productResponse,
				sampleList:
					item?.itemList?.map((list) => {
						rowIdCount += 1;
						return {
							...list,
							rowId: rowIdCount
						};
					}) || []
			});

			sampleItemsList.push({
				...item?.itemList,
				productResponse: item?.productResponse
			});
		});

		samples.forEach((item) => {
			item.sampleList.map((sample) => (sample.productResponse = createProductResponse(item)));
		});

		const checkResponseLength = sampleItemsList.filter((item) => isValidRow(item));
		setResponseLength(checkResponseLength?.length);

		samples.forEach((item) => {
			if (item?.sampleList?.length < 2) {
				Array.from({ length: 2 - item?.sampleList?.length }).forEach(() => {
					rowIdCount += 1;
					return item?.sampleList.push({
						productResponse: {
							...createProductResponse(item)
						},
						sampleId: null,
						countryResponse: {},
						brandResponse: {},
						supplierResponse: {},
						qcApprovalDate: null,
						color: null,
						size: null,
						sampleType: null,
						sampleStatus: null,
						remarks: null,
						sampleInventoryLocationId: null,
						rowId: rowIdCount + 1
					});
				});
			}
		});

		return samples;
	};

	const isValidRow = (sample) => {
		const { productResponse, countryResponse, sampleId, qcApprovalDate, sampleStatus, sampleType } =
			sample || {};

		return (
			(sampleId &&
				qcApprovalDate &&
				sampleStatus &&
				sampleType &&
				productResponse?.isNitexProduct &&
				countryResponse?.id) ||
			(sampleId && qcApprovalDate && sampleStatus && sampleType)
		);
	};

	const getProductsSamples = async () => {
		try {
			dispatch(openLoader());
			const response = await getProductSamples(productIds?.join(','), 'LIST_VIEW');

			const data = organizeResponseData(response);
			setProductSampleList(data);
			// setBaseSizeMap(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		const allBrands = brandsList?.map((brand) => ({
			...brand,
			value: brand?.name
		}));
		setAllBrandsList(allBrands);
	}, [brandsList.length]);

	const getSampleLocations = async (searchKey = '') => {
		try {
			const params = `?search=${searchKey}&page=0&size=20`;
			dispatch(openLoader());
			const response = await getProductSampleLocations(params);
			const locationsArr = response?.map((item) => ({
				...item,
				secondLine: [
					item?.countryResponse?.code,
					item?.buildingTagResponse?.text,
					item?.floorTagResponse?.text,
					item?.roomOrAreaTagResponse?.text,
					item?.otherDetails
				]
					.filter(Boolean)
					.join(', ')
			}));

			setLocationsList(locationsArr);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const getAllSuppliers = async (searchKey = '') => {
		try {
			dispatch(openLoader());
			const params = `?page=0&size=20&name=${searchKey}&materialType=GARMENT&sort=id,desc`;
			const { data = [] } = await findAllSupplier(params);

			const suppliersList = data?.map((item) => ({
				...item,
				value: item?.name
			}));

			setSuppliersList(suppliersList);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const fetchCountryList = async () => {
		try {
			dispatch(openLoader());
			const response = await getSampleCountryList();
			setCountryList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleWrongEntry = async (sampleId, isWrongEntry) => {
		try {
			dispatch(openLoader());
			await updateSampelInventory(sampleId, isWrongEntry);
			await getProductsSamples();
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		getProductsSamples();
		fetchCountryList();
		getAllSuppliers();
		dispatch(getBrands(`?page=0&size=1000`));
		getSampleLocations();
		fetchSampleLocations();
	}, []);

	const handleInputChange = (e, rowId) => {
		const { value, name } = e.target;
		const updatatedSamples = productSampleList.map((item) => ({
			...item,
			sampleList: item?.sampleList?.map((sample) => {
				if (sample?.rowId === rowId) {
					if (name === 'sampleId') {
						return {
							...sample,
							[name]: value,
							qcApprovalDate: new Date().toISOString().split('T')[0],
							sampleStatus: 'AVAILABLE'
						};
					} else {
						return {
							...sample,
							[name]: value
						};
					}
				}
				return sample;
			})
		}));
		setProductSampleList(updatatedSamples);
	};

	const handleSelectOption = (value, optionName, rowId, isObject = false) => {
		const updatedSampleList = productSampleList.map((item) => ({
			...item,
			sampleList: item?.sampleList.map((sample) => {
				if (sample?.rowId === rowId) {
					if (optionName === 'sampleType' && value !== 'BUYER_TP_DEVELOPMENT') {
						return {
							...sample,
							[optionName]: isObject ? { ...value } : value,
							brandResponse: {},
							supplierResponse: {}
						};
					} else if (optionName === 'sampleType' && value === 'BUYER_TP_DEVELOPMENT') {
						if (!sample?.brandResponse?.id) {
							return {
								...sample,
								[optionName]: isObject ? { ...value } : value,
								brandResponse: {
									id: item?.brandId,
									value: item.brandName,
									name: item?.brandName
								}
							};
						}
						return {
							...sample,
							brandResponse: {}
						};
					} else {
						return {
							...sample,
							[optionName]: isObject ? { ...value } : value
						};
					}
				}
				return sample;
			})
		}));
		setProductSampleList(updatedSampleList);
	};

	const handleChangeCheckbox = (e) => {
		let copySampleIds = [...selectedSampleIds];
		if (e.target.checked) {
			copySampleIds = [...copySampleIds, e.target.value];
			setSelectedSampleIds(copySampleIds);
		} else {
			setSelectedSampleIds(selectedSampleIds.filter((id) => String(id) !== String(e.target.value)));
		}
	};

	const getAllTheSampleIds = () => {
		const allSampleIds = [];

		if (productSampleList) {
			const copySamples = [...productSampleList];
			copySamples.forEach((item) => {
				if (item && item.sampleList) {
					item.sampleList.forEach((sample) => {
						if (sample && isValidRow(sample)) {
							if (sample?.sampleId) {
								allSampleIds.push(sample.sampleId);
							}
						}
					});
				}
			});
		}
		return allSampleIds;
	};

	const checkAll = (value) => {
		const ids = getAllTheSampleIds();
		setSelectedSampleIds(value ? ids : []);
	};

	const fetchSampleLocations = async (searchKey = '') => {
		try {
			const params = `?search=${searchKey}&page=0&size=20`;
			dispatch(openLoader());
			const response = await getProductSampleLocations(params);

			setLocationsList(response);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const validateRequestBody = (requestBody) => {
		const errors = [];

		for (const item of requestBody) {
			if (item?.sampleId) {
				// Factory brought sample should have supplier id
				if (
					!item?.isWrongEntry &&
					item?.sampleType === 'FACTORY_BROUGHT_SAMPLE' &&
					!item?.supplierId
				) {
					errors.push({
						supplierId: 'Please Select the Factory Name',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}

				// Shipping sample should have brand id
				if (!item?.isWrongEntry && item?.sampleType === 'SHOPPING_SAMPLE' && !item?.brandId) {
					errors.push({
						brandId: 'Please Select Brand',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}
				// Sample Type
				if (!item?.isWrongEntry && !item?.sampleType) {
					errors.push({
						sampleType: 'Sample Type is missing',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}
				// qcApprovalDate
				if (!item?.isWrongEntry && !item?.qcApprovalDate) {
					errors.push({
						qcApprovalDate: 'QC Approval Date missing',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}
				// If Nitex product Should select countryID
				if (!item?.isWrongEntry && item?.isNitexProduct && !item?.countryId) {
					errors.push({
						countryId: 'Please Select Country',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}
				// Sample Status
				if (!item?.isWrongEntry && !item?.sampleStatus) {
					errors.push({
						sampleStatus: 'Please Select sample Status',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}
				// Sample ID
				if (!item?.isWrongEntry && !item?.sampleId) {
					errors.push({
						sampleId: 'Please Select Sample Id',
						rowId: item?.rowId,
						productId: item?.productId
					});
				}
			}
		}

		productSampleList.forEach((item) => {
			return item?.sampleList.forEach((sample) => {
				if (!item?.isWrongEntry && sample?.id && !sample?.productResponse?.fabricName) {
					errors.push({
						bodyFabric: 'Please Add Body fabric',
						rowId: sample?.rowId,
						productId: sample?.productResponse?.id
					});
				}
			});
		});

		if (errors.length > 0) {
			setErrors(errors);
			return false;
		}
		return true;
	};

	const createRequestBody = () => {
		const requestBody = [];
		const copySampleList = [...productSampleList];

		copySampleList.forEach((item) => {
			return item?.sampleList.forEach((sample) => {
				if (!sample?.productResponse?.parentProductId) {
					const body = {
						id: sample?.id,
						productId: sample?.productResponse?.id,
						sampleId: sample?.sampleId,
						countryId: sample?.countryResponse?.id,
						qcApprovalDate: sample?.qcApprovalDate,
						color: sample?.color,
						size: sample?.size,
						sampleType: sample?.sampleType,
						sampleStatus: sample?.sampleStatus || (sample?.sampleId && 'AVAILABLE'),
						remarks: sample?.remarks,
						brandId: sample?.brandResponse?.id,
						supplierId: sample?.supplierResponse?.id,
						rowId: sample?.rowId,
						sampleInventoryLocationId: sample?.sampleInventoryLocationResponse?.id,
						isNitexProduct: sample?.productResponse?.isNitexProduct,
						isWrongEntry: sample?.isWrongEntry
					};
					// Filter out null or undefined values
					const cleanedBody = Object.fromEntries(
						Object.entries(body).filter(([, value]) => value != null)
					);

					requestBody.push(cleanedBody);
				}
			});
		});

		return requestBody.filter((item) => item.sampleId);
	};

	const getSelectedSamplesBodyForDownload = () => {
		const body = createRequestBody();
		const downloadBody = body.filter((item) => selectedSampleIds.includes(item.sampleId));
		return downloadBody;
	};

	const handleDownloadSelectedSample = async () => {
		try {
			dispatch(openLoader());
			const requestBody = getSelectedSamplesBodyForDownload();
			const queryParams = `?productIds=${productIds?.join(
				','
			)}&sampleIdentifierNos=${selectedSampleIds.join(',')}`;
			const { data } = await ApiClient.POST('downloadInventoryStickers', requestBody, queryParams);
			setIsShowInventoryModal(false);
			window.open(data, '_blank');
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSubmitInfo = async () => {
		try {
			dispatch(openLoader());
			const body = createRequestBody();
			const isValid = validateRequestBody(body);
			if (!isValid || body.length <= 0) {
				return;
			}

			await submitSampleUpdate(body);
			await getProductsSamples();
			setErrors([]);
			toastSuccess('Update Successful!');
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleNavigateToMaterials = (productId) => {
		let url = `/designs/edit/${productId}`;
		let queryParams = new URLSearchParams();
		queryParams.set('activeTab', 'materials');
		window.open(`${url}?${queryParams.toString()}`, '_blank');
	};

	const getBaseSize = (sample) => {
		const designId = sample?.productResponse?.id ?? '';
		const nitexDevelopment =
			sample?.baseSizeMap?.[designId]?.NITEX_DEVELOPMENT || sample?.baseSizeMap?.[designId]?.PP_SAMPLE;
		return nitexDevelopment;
	};

	const shouldDisableCheckBox = (sample) => {
		return (
			sample?.isWrongEntry ||
			!sample?.sampleId ||
			!sample?.qcApprovalDate ||
			(sample?.productResponse?.isNitexProduct && !sample?.countryResponse?.id) ||
			!sample?.sampleStatus ||
			!sample?.sampleType
		);
	};

	const tableRow = ({ sample, index, rowSpan = 1, showStyleColumn, showBodyFabricColumn }) => {
		const findError = (field) => {
			return errors.find(
				(error) =>
					error.rowId === sample?.rowId &&
					error.productId === sample?.productResponse?.id &&
					error[field]
			);
		};
		return (
			<tr key={`${index}-${sample?.id}-${rowSpan}`}>
				<td className={`checkbox-column ${shouldDisableCheckBox(sample) ? 'dissable-td' : ''}`}>
					<div className="custom-chekbox d-inline-block align-middle">
						<div className="form-group m-0">
							<input
								type="checkbox"
								id={`costingSheetAll__${sample?.sampleId}`}
								name={`costingSheetAll__${sample?.sampleId}`}
								value={sample?.sampleId}
								disabled={shouldDisableCheckBox(sample)}
								onChange={(e) => handleChangeCheckbox(e)}
								checked={selectedSampleIds?.find(
									(value) => String(value) === String(sample?.sampleId)
								)}
							/>
							<label htmlFor={`costingSheetAll__${sample?.sampleId}`} />
						</div>
					</div>
				</td>
				{showStyleColumn && (
					<td className="style-column" rowSpan={rowSpan}>
						<div className="d-flex align-items-center">
							<div className="design-image">
								<CommonImage
									src={
										sample?.productResponse?.designDocuments
											? sample?.productResponse?.designDocuments[0]?.docUrl
											: ''
									}
									alt="image"
									className="cell-img pr-2"
									isStatic={false}
								/>
							</div>
							<div className="ml-12">
								<p className="regular-12 gray_dark_01 py-1">
									{sample?.productResponse?.referenceNumber}
								</p>
								<ul className="common-list-component mb-2">
									<li>{sample?.productResponse?.productGroup}</li>
									<li>{sample?.productResponse?.category}</li>
								</ul>
							</div>
						</div>
					</td>
				)}
				<td
					className={`sample-td ${findError('sampleId') ? 'error-input' : ''}${
						canEditSampleId(sample, isDesignEdit) ? '' : 'dissable-td'
					}`}
				>
					{canEditSampleId(sample, isDesignEdit) ? (
						<>
							<InputField
								type="text"
								value={sample?.sampleId || ''}
								onChangeHandler={(e) => handleInputChange(e, sample?.rowId)}
								name="sampleId"
								id="sampleId"
								placeholder="Sample Id"
								style={{ background: findError('sampleId') ? '#FFEAEA' : '#FFF' }}
							/>
						</>
					) : (
						<p className={`${sample?.isWrongEntry ? 'regular-14 gray_dark_02' : ''}`}>
							{sample?.isWrongEntry ? (
								<span>
									<del>{sample?.sampleId}</del>
								</span>
							) : (
								<span>{sample?.sampleId}</span>
							)}
							{sample?.isWrongEntry && <span className="regular-14 red">(Wrong Entry)</span>}
						</p>
					)}
				</td>
				<td
					className={`country-column ${findError('countryId') ? 'error-td' : ''} ${
						canEditCountry(
							sample,
							isDesignEdit,
							null,
							sample?.productResponse?.isNitexProduct ? 'YES' : 'NO'
						)
							? ''
							: 'dissable-td'
					}`}
				>
					{canEditCountry(
						sample,
						isDesignEdit,
						null,
						sample?.productResponse?.isNitexProduct ? 'YES' : 'NO'
					) ? (
						<CustomDropdown
							isShowSearch={false}
							title="Select"
							items={countryList}
							selectedItem={sample?.countryResponse?.code}
							onItemClick={
								(value) => handleSelectOption(value, 'countryResponse', sample?.rowId, true) // isObject=true
							}
						/>
					) : (
						<p>{sample?.countryResponse?.code}</p>
					)}
				</td>
				<td
					className={`qc-date ${findError('qcApprovalDate') ? 'error-input' : ''} ${
						canEditQcApproval(isDesignEdit, sample) ? '' : 'dissable-td'
					}`}
				>
					{canEditQcApproval(isDesignEdit, sample) ? (
						<InputField
							type="date"
							value={sample?.qcApprovalDate || ''}
							onChangeHandler={(e) => handleInputChange(e, sample?.rowId)}
							id="qcApprovalDate"
							name="qcApprovalDate"
						/>
					) : (
						<p>{sample?.qcApprovalDate}</p>
					)}
				</td>

				<td
					className={`type-column ${
						findError('sampleType') ? 'type-column error-td' : 'type-column'
					} ${canEditDropdown(sample, isDesignEdit) ? '' : 'dissable-td'}`}
				>
					{canEditDropdown(sample, isDesignEdit) ? (
						<CustomDropdown
							title="Select"
							items={SAMPLE_TYPES}
							selectedItem={getSampleTypeBasedOnConditions(sample)}
							onItemClick={(value) => {
								handleSelectOption(value?.id, 'sampleType', sample?.rowId);
							}}
						/>
					) : (
						<p>{replaceUnderLine(sample?.sampleType)}</p>
					)}
				</td>
				<td
					className={`${
						findError('brandId') ? 'error-td' : '' || findError('supplierId') ? 'error-td' : ''
					}  ${canEditBrandOrFactory(sample) ? '' : 'dissable-td'}`}
				>
					{canEditBrandOrFactory(sample) ? (
						<CustomDropdown
							title="Select"
							onItemSearch={(value) => {
								isFactory(sample?.sampleType)
									? getAllSuppliers(value)
									: dispatch(getBrands(`?page=0&size=1000&name=${value}`));
							}}
							items={isFactory(sample?.sampleType) ? suppliersList : allBrandsList}
							selectedItem={
								isFactory(sample?.sampleType)
									? sample?.supplierResponse?.name
									: sample?.brandResponse?.name
							}
							onItemClick={(value) =>
								handleSelectOption(
									value,
									isFactory(sample?.sampleType) ? 'supplierResponse' : 'brandResponse',
									sample?.rowId,
									true
								)
							}
						/>
					) : (
						<p>{sample?.supplierResponse?.name || sample?.brandResponse?.name}</p>
					)}
				</td>

				<td className={`${canEditDropdown(sample, isDesignEdit) ? 'color' : 'dissable-td'}`}>
					{canEditDropdown(sample, isDesignEdit) ? (
						<InputField
							type="text"
							value={sample?.color || ''}
							onChangeHandler={(e) => handleInputChange(e, sample?.rowId)}
							id="color"
							name="color"
						/>
					) : (
						<p>{sample?.color}</p>
					)}
				</td>
				<td className={`${canEditDropdown(sample, isDesignEdit) ? 'size' : 'dissable-td'}`}>
					{canEditDropdown(sample, isDesignEdit) ? (
						<InputField
							type="text"
							value={sample.size || getBaseSize(sample) || ''}
							onChangeHandler={(e) => handleInputChange(e, sample?.rowId)}
							id="size"
							name="size"
						/>
					) : (
						<p>{sample?.size || getBaseSize(sample)}</p>
					)}
				</td>

				<td
					className={`${
						canEditLocation(sample, isDesignEdit)
							? 'location-column'
							: 'dissable-td location-column'
					}`}
				>
					{canEditLocation(sample, isDesignEdit) ? (
						<CustomDropdown
							title="Select"
							items={locationsList}
							onAddNewButtonClick={(value, setIsOpen) => {
								setShowLocationModal(true);
								setActiveRow(sample?.rowId);
								setSelectedLocation(value);
								setIsOpen(false);
							}}
							onItemSearch={(value) => getSampleLocations(value)}
							onEdit={(value) => {
								setShowLocationModal(true);
								setSelectedLocation({ ...value, isEdit: true });
								setActiveRow(sample?.rowId);
							}}
							showAddNewButton
							selectedItem={locationInfo(sample?.sampleInventoryLocationResponse)}
							isEditable={true}
							onItemClick={(value) =>
								handleSelectOption(value, 'sampleInventoryLocationResponse', sample?.rowId)
							}
						/>
					) : (
						<p>{locationInfo(sample?.sampleInventoryLocationResponse)}</p>
					)}
				</td>
				<td className={`${!sample?.isWrongEntry ? 'location-column' : 'dissable-td'}`}>
					{sample?.sampleInventoryLocationAddedBy?.nickName && (
						<p className="regular-14 gray_dark_02">
							{sample?.sampleInventoryLocationAddedBy?.nickName}
						</p>
					)}

					{sample?.sampleInventoryLocationAddedAt && (
						<p className="regular-12 gray_dark_03">
							{convertDateToLocal(sample?.sampleInventoryLocationAddedAt, 'DD MMM, YYYY')}
						</p>
					)}
				</td>
				{showBodyFabricColumn && (
					<td
						rowSpan={rowSpan}
						className={`${findError('bodyFabric') ? 'fabric-column error-td' : 'fabric-column'} ${
							!sample?.productResponse?.fabricName ? 'dissable-td' : 'fabric-column'
						}`}
						style={{ background: findError('bodyFabric') ? '#FFEAEA' : '#FFF' }}
					>
						{sample?.productResponse?.fabricName ? (
							<p className="d-flex regular-14 gray_dark_01 align-items-center">
								<img
									src={`${process.env.PUBLIC_URL}/icons/Check-large.svg`}
									alt=""
									width={18}
								/>{' '}
								<span className="pl-1">Fabric Added</span>
							</p>
						) : (
							<p
								className="d-flex regular-14 gray_dark_01 align-items-center cursor-pointer"
								onClick={() => handleNavigateToMaterials(sample?.productResponse?.id)}
							>
								<span className="pl-1 blue ">Add Fabric</span>
								<img src={`${process.env.PUBLIC_URL}/icons/arrow-forward-blue.svg`} alt="" />
							</p>
						)}
					</td>
				)}
				<td className={`remark-td ${canEditRemarks(sample, isDesignEdit) ? '' : 'dissable-td'}`}>
					{canEditRemarks(sample, isDesignEdit) ? (
						<InputField
							type="text"
							value={sample?.remarks || ''}
							onChangeHandler={(e) => handleInputChange(e, sample?.rowId)}
							id="remarks"
							name="remarks"
						/>
					) : (
						<p>{sample?.remarks}</p>
					)}
				</td>
				<td
					className={`${findError('sampleStatus') ? 'error-td' : ''}${
						canEditDropdown(sample, isDesignEdit) ? '' : 'dissable-td'
					}`}
				>
					{canEditDropdown(sample, isDesignEdit) ? (
						<CustomDropdown
							title="Select"
							items={SAMPLE_STATUS}
							selectedItem={replaceUnderLine(sample?.sampleStatus)}
							onItemClick={(value) =>
								handleSelectOption(value?.id, 'sampleStatus', sample?.rowId)
							}
						/>
					) : (
						<p>{replaceUnderLine(sample?.sampleStatus)}</p>
					)}
				</td>
				{isAllowedToEditInventory() && (
					<td
						className={`${
							isAllowedToEditInventory() && sample?.sampleId
								? 'cursor-pointer action-column'
								: 'cursor-pointer action-column dissable-td'
						}`}
					>
						{sample?.sampleId && (
							<TableActionDropdown
								rowId={sample?.rowId}
								selectedRowId={selectedRowId}
								setSelectedRowId={setSelectedRowId}
								onWrongEntry={handleWrongEntry}
								sampleDetails={sample}
							/>
						)}
					</td>
				)}
			</tr>
		);
	};

	const renderTableBody = () => {
		return (
			<>
				{productSampleList?.map((item, index) => (
					<React.Fragment key={`ROW-${index}`}>
						{tableRow({
							sample: item?.sampleList[0],
							index,
							rowSpan: item?.sampleList?.length,
							showStyleColumn: true,
							showBodyFabricColumn: true,
							checkBoxInputId: 'TOP_ROW'
						})}
						{item?.sampleList?.slice(1)?.map((sample, index) =>
							tableRow({
								sample,
								index,
								rowSpan: 1,
								showStyleColumn: false,
								showBodyFabricColumn: false,
								checkBoxInputId: sample?.rowId
							})
						)}
					</React.Fragment>
				))}
			</>
		);
	};
	return (
		<ModalContainer className="sample-table-modal" onCloseModal={onClose}>
			<div className="wraper container-fluid dashboard-container">
				<div className="popup-wrapper">
					<div className="common-popup sample-table-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Inventory & Sticker</h3>
							</div>
							<div className="close-btn" onClick={onClose}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<ScrollButtons scrollButtonRef={scrollButtonRef} />
						<div
							className="common-popup-body p-0 scroll-y-label scroll-x-label"
							ref={scrollButtonRef}
						>
							<div className="sample-table sample-inventory-sticker">
								<table className="table with-border no-hover height-sm hover">
									<thead>
										<tr className="heading-row">
											<th className="checkbox-column">
												<div className="custom-chekbox d-inline-block align-middle">
													<div className="form-group m-0">
														<input
															type="checkbox"
															id={`costingSheetAll-${id}`}
															name={`costingSheetAll-${id}`}
															disabled={responseLength <= 0}
															onChange={(e) => {
																checkAll(e.target.checked);
															}}
															checked={isAllSelected}
														/>
														<label htmlFor={`costingSheetAll-${id}`}></label>
													</div>
												</div>
											</th>

											<th className="style-column">Style</th>
											<th className="sample-column sample-td white-space-nowrap">
												Sample ID*
											</th>
											<th className="country-column">Country*</th>
											<th>QC Approval</th>
											<th>Sample Type*</th>
											<th>Brand/Factory</th>
											<th>Color</th>
											<th>Size</th>
											<th>Sample Location</th>
											<th>Location Updated by</th>
											<th>Body Fabic*</th>
											<th>Remarks</th>
											<th>Sample Status</th>
										</tr>
									</thead>
									<tbody>{renderTableBody()}</tbody>
								</table>
							</div>
						</div>
						<div className="modal-footer">
							<button
								className="button gray-outline size32"
								onClick={() => getProductsSamples()}
							>
								<span>
									<img src={`${process.env.PUBLIC_URL}/icons/reload-icon.svg`} alt="" />
								</span>{' '}
								Reload
							</button>
							<div className="footer-buttons text-right">
								<button className="button gray-outline size32" onClick={handleSubmitInfo}>
									Update Info
								</button>
								<button
									className="button size32 ml-2"
									disabled={selectedSampleIds.length <= 0}
									onClick={handleDownloadSelectedSample}
								>
									Download
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			{showLocationModal && (
				<AddLocationModal
					onClose={() => setShowLocationModal(false)}
					countryList={countryList}
					locationsList={locationsList}
					selectdLocationToEdit={selectedLocation}
					setShowAddLocationModal={setShowLocationModal}
					activeRow={activeRow}
					setAllSampleList={setProductSampleList}
					parentComponent="INVENTORY_MODAL"
				/>
			)}
		</ModalContainer>
	);
};

export default InventoryAndStickerModal;
